import { useEffect, useState, useRef } from "react";
import { usePublication } from "../../../hooks/usePublication";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  Section,
  SubSection,
  Fragment,
  findTypeOption,
  typeLibro,
} from "../../../interfaces/Publication";
import FragmentContent from "../Fragments/FragmentContent";

const FullscreenFragmentViewer = () => {
  const { publication } = usePublication();
  const location = useLocation();
  const [currentSection, setCurrentSection] = useState<Section | null>(null);
  const [currentSubsection, setCurrentSubsection] = useState<SubSection | null>(
    null
  );
  const [currentFragment, setCurrentFragment] = useState<Fragment | null>(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(-1);
  const [isVisible, setIsVisible] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  let { idRevista, idLibro } = useParams();
  const typeLibro: typeLibro = idLibro ? "libro" : "revista";
  const route = findTypeOption(typeLibro)?.route || "";
  const [isHover, setIsHover] = useState(false);

  // Modificar la función findSectionData para que también guarde el índice
  const findSectionData = (
    sectionId: string,
    subsectionId: string,
    fragmentId?: string
  ) => {
    const sectionIdNum = parseInt(sectionId, 10);
    const subsectionIdNum = parseInt(subsectionId, 10);
    const fragmentIdNum = fragmentId ? parseInt(fragmentId, 10) : null;

    // Encontrar la sección y su índice
    const foundSectionIndex = publication.sections.findIndex(
      (section) => section.id.toString() === sectionIdNum.toString()
    );

    const foundSection = publication.sections[foundSectionIndex];

    let foundSubsection: SubSection | undefined;
    let foundFragment: Fragment | undefined;

    if (foundSection) {
      foundSubsection = foundSection.subsections?.find(
        (subsection) => subsection.id.toString() === subsectionIdNum.toString()
      );

      if (foundSubsection && fragmentIdNum) {
        foundFragment = foundSubsection.fragments.find(
          (fragment) => fragment.id.toString() === fragmentIdNum.toString()
        );
      }
    }

    setCurrentSection(foundSection || null);
    setCurrentSectionIndex(foundSectionIndex);
    setCurrentSubsection(foundSubsection || null);
    setCurrentFragment(foundFragment || null);
    setIsVisible(!!foundSection && !!foundSubsection);
  };

  const closeViewer = () => {
    setIsVisible(false);
    setCurrentSection(null);
    setCurrentSubsection(null);
    setCurrentFragment(null);
    setCurrentSectionIndex(-1);

    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("section");
    searchParams.delete("modal");
    searchParams.delete("fragment");
    const newUrl = `${location.pathname}${
      searchParams.toString() ? "?" + searchParams.toString() : ""
    }`;
    window.history.replaceState({}, "", newUrl);
  };

  useEffect(() => {
    const updateFromURL = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const sectionId = searchParams.get("section");
      const modalId = searchParams.get("modal");
      const fragmentId = searchParams.get("fragment");

      if (sectionId && modalId) {
        findSectionData(sectionId, modalId, fragmentId || undefined);
      } else {
        setIsVisible(false);
        setCurrentSection(null);
        setCurrentSubsection(null);
        setCurrentFragment(null);
        setCurrentSectionIndex(-1);
      }
    };

    updateFromURL();
    window.addEventListener("urlModalChange", updateFromURL);
    window.addEventListener("popstate", updateFromURL);

    return () => {
      window.removeEventListener("urlModalChange", updateFromURL);
      window.removeEventListener("popstate", updateFromURL);
    };
  }, [location.key, publication]);

  const renderContent = () => {
    if (!currentSubsection) {
      return (
        <div className="text-white text-center">
          No se encontró la subsección
        </div>
      );
    }

    if (currentFragment) {
      return (
        <FragmentContent
          key={currentFragment.id}
          fragment={currentFragment}
          openButtons={{
            optConfig: false,
            optIndex: false,
            optNotes: false,
            optFileDoc: false,
            optReviews: false,
            optHelp: false,
            optAuthor: false,
            optReferences: false,
            optIA: false,
          }}
          setOpenButtons={() => { } }
          rawMode={true} index={0}        />
      );
    }

    return currentSubsection.fragments.map((fragment) => (
      <FragmentContent
        key={fragment.id}
        fragment={fragment}
        openButtons={{
          optConfig: false,
          optIndex: false,
          optNotes: false,
          optFileDoc: false,
          optReviews: false,
          optHelp: false,
          optAuthor: false,
          optReferences: false,
          optIA: false,
        }}
        setOpenButtons={() => { } } index={0}      />
    ));
  };

  // Cerrar con ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isVisible) {
        closeViewer();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      className="fullscreen-viewer-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        animation: "fadeIn 0.3s ease-in-out",
      }}
    >
      {/* Header mínimo */}
      <div
        className="viewer-header"
        style={{
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center", 
          padding: "10px 20px",
          backgroundColor: "rgb(0, 0, 0)",
          color: "white",
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          fontWeight: "600",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          height: "64px",
          boxSizing: "border-box",
        }}
      >
        {/* Bloque del título */}
        <div
          style={{
            position: "relative",
            flex: 1,
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <span style={{ fontSize: "15px", fontWeight: 600, color: "white" }}>
            <strong>
              {`${
                currentFragment?.title || currentFragment?.name || "Figura 1.11"
              } `}
            </strong>
            Instrumentos para medir la masa de un objeto
          </span>

          {/* "Ir al capítulo" pegado al fondo sin afectar layout */}
          <Link
            to={`/${route}/${publication.id}/${currentSectionIndex}`}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            style={{
              position: "absolute",
              right: "25px",
              bottom: "-10px",
              color: isHover ? "#ffa500" : "#3BA8F2", 
              fontWeight: 600,
              fontSize: "15px",
              textDecoration: "none",
              transition: "color 0.1s ease",
              cursor: "pointer",
            }}
          >
            Ir al capítulo
          </Link>
        </div>

        {/* Botón cerrar circular y centrado verticalmente con el título */}
        <button
          aria-label="Cerrar ventana de figura"
          onClick={closeViewer}
          style={{
            padding: 0,
            border: "none",
            boxSizing: "border-box",
            width: "38px",
            height: "38px",
            minWidth: "38px",
            minHeight: "38px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "normal",
            borderRadius: "50%",
            overflow: "hidden",
            backgroundColor: "#E44E67",
            color: "#fff",
            cursor: "pointer",
            flexShrink: 0,
            transform: "none",
          }}
        >
          <span
            className="btn__icon"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              fontSize: "17px",
              lineHeight: 1,
            }}
          >
            <i className="icon icon-vpro-close" style={{ display: "block" }} />
          </span>
        </button>
      </div>

      {/* Content Area - SIN scaling, el contenido se renderiza naturalmente */}
      <div
        className="viewer-content"
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: "0",
          overflow: "auto",
        }}
      >
        <div
          ref={contentRef}
          className="content-container"
          style={{
            background: "rgba(0, 0, 0, 0.2)",
            paddingRight: "20px",
            paddingLeft: "20px",
            maxWidth: "100%",
            minHeight: "100%",
            boxShadow: "0 0 0 rgba(0, 0, 0, 0)",
            width: "100vw",
            margin: "0 auto",
          }}
        >
          {renderContent()}
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          .fullscreen-viewer-overlay {
            animation: fadeIn 0.3s ease-in-out;
          }

          .viewer-content::-webkit-scrollbar {
            width: 8px;
          }
          
          .viewer-content::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
          }
          
          .viewer-content::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 4px;
          }
          
          .viewer-content::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        `}
      </style>
    </div>
  );
};

export default FullscreenFragmentViewer;
